/**
 * stage-summary — the one-line result each pipeline stage produced.
 *
 * The side column's job is to give enough status that a person does not have
 * to open every report to learn things went fine. So each agent row says what
 * it *produced* — "12 sources kept", "Draft v4", "Score 92" — rather than
 * repeating "Done" eight times.
 *
 * Returns `null` when the number genuinely is not stored, and the caller
 * falls back to the plain status word. Two of the eight lines the design asks
 * for are null today and say so in place:
 *
 *   - **Scout's source count.** `pieces.source_urls` looks like it would hold
 *     this, but it is a migration traceability tag (`directus:id=N`), not a
 *     source list. The count of URLs Scout found is not persisted anywhere;
 *     what survives is what the Librarian kept, which is the next row.
 *   - **Auditor's fact count.** The fact-check report exists as a document,
 *     but the number of claims checked and flagged is only inside its prose.
 *     Extracting a count is the same missing per-piece figure as S-380.
 *
 * Inventing either would put a confident number on screen that no data backs.
 */

export interface StageFacts {
  /** Reference cards the Librarian kept. */
  referenceCards: number;
  /** Highest draft version present, or null when nothing is drafted. */
  latestDraftVersion: number | null;
  imageCount: number;
  qualityScore: number | null;
  seoScore: number | null;
  /**
   * FEAT-084 — sum of the four voice dimensions, out of 40, or null when the
   * evaluator has not run or its response did not parse. Null renders no chip
   * rather than a zero, which would read as a catastrophic score.
   */
  voiceAggregate?: number | null;
  hasResearchReport: boolean;
  hasFactCheck: boolean;
  /** Already formatted to the reader's date setting, or null. */
  publishedLabel: string | null;
  /** The piece's status, for the Publisher row's failure case. */
  status: string;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * The content and fiction pipelines have different phase values but the same
 * shape of work, so both map onto the same summaries where they line up.
 */
export function stageSummary(
  phaseValue: string,
  facts: StageFacts
): string | null {
  switch (phaseValue) {
    // ── Research ──────────────────────────────────────────────────────
    case "phase_1_researched":
    case "fiction_phase_1_worldbuilt":
      return facts.hasResearchReport ? "Researched" : null;

    case "phase_2_outlined":
    case "fiction_phase_2_architected":
      return facts.referenceCards > 0
        ? plural(facts.referenceCards, "source kept", "sources kept")
        : null;

    // ── Drafting ──────────────────────────────────────────────────────
    case "phase_3_draft_generated":
    case "fiction_phase_3_draft_generated":
      return facts.latestDraftVersion != null
        ? `Draft v${facts.latestDraftVersion}`
        : null;

    // ── Checking ──────────────────────────────────────────────────────
    case "phase_4_fact_checked":
      return facts.hasFactCheck ? "Fact-checked" : null;
    case "fiction_phase_4_continuity_checked":
      return facts.hasFactCheck ? "Continuity checked" : null;

    case "phase_5_corrected":
    case "fiction_phase_5_corrected":
      return facts.qualityScore != null ? `Score ${facts.qualityScore}` : null;

    // FEAT-084 — the aggregate of the four voice dimensions, out of 40. The
    // gate's own threshold, so the chip shows the number the gate reads.
    case "phase_5b_voice_scored":
      return facts.voiceAggregate != null ? `Voice ${facts.voiceAggregate}/40` : null;

    case "phase_6_seo_optimized":
      return facts.seoScore != null ? `SEO ${facts.seoScore}` : null;

    // ── Art ───────────────────────────────────────────────────────────
    case "phase_7_art_directed":
    case "fiction_phase_6_illustrated":
      return facts.imageCount > 0
        ? plural(facts.imageCount, "image", "images")
        : null;

    // ── Publish ───────────────────────────────────────────────────────
    case "phase_8_published":
    case "fiction_phase_7_published":
      if (facts.status === "failed") return "Failed";
      return facts.publishedLabel;

    default:
      return null;
  }
}
